import { RabEntry } from '../../types';

/**
 * EINE Reihenfolge für die Unterlage für spätere Arbeiten.
 *
 * Bis 09.09.2026 gab es drei: die Eintragsliste gruppierte nach Kategorie und
 * sortierte die Gruppen nach der Einstellung, die HTML-Vorschau lief flach
 * durch die Liste und setzte bei jedem Kategoriewechsel eine neue Überschrift
 * (dieselbe Kategorie also mehrfach), das PDF gruppierte nach Erstauftreten.
 * Die Pfeile „nach oben/unten" tauschten in der FLACHEN Liste — mit dem
 * Nachbarn einer anderen Kategorie war der Tausch in der App unsichtbar und
 * änderte im PDF die Reihenfolge der Kategorien.
 *
 * Jetzt lesen Liste und PDF dieselbe Gruppierung aus dieser Datei; die
 * Vorschau zeigt das echte PDF.
 */

export const DEFAULT_CATEGORY_ORDER = [
    'Dach', 'Fassade', 'Außenanlage', 'Technik', 'Innenraum', 'Verkehrswege', 'Sonstiges',
];

export const FALLBACK_CATEGORY = 'Sonstiges';

/**
 * Vergleichsschlüssel einer Kategorie: Groß/Klein und Randleerzeichen sind
 * egal, „Außenanlagen" (so schrieb die automatische Zuordnung jahrelang) ist
 * dieselbe Kategorie wie „Außenanlage" aus der Standardreihenfolge.
 */
export const categoryKey = (category: string | undefined | null): string => {
    const key = (category || '').trim().toLowerCase();
    if (!key) return FALLBACK_CATEGORY.toLowerCase();
    if (key === 'außenanlagen') return 'außenanlage';
    return key;
};

/** Anzeigename: leer wird zu „Sonstiges", sonst der Text ohne Randleerzeichen. */
export const displayCategory = (category: string | undefined | null): string =>
    (category || '').trim() || FALLBACK_CATEGORY;

export interface CategoryGroup {
    /** Anzeigename, wie er beim ersten Eintrag der Gruppe geschrieben ist. */
    category: string;
    entries: RabEntry[];
}

/**
 * Gruppiert die Einträge nach Kategorie. Bekannte Kategorien in der
 * eingestellten Reihenfolge, unbekannte dahinter in der Reihenfolge ihres
 * ersten Auftretens. Innerhalb einer Gruppe bleibt die Listenreihenfolge —
 * das ist die Reihenfolge, die die Pfeile verändern.
 */
export const groupEntriesByCategory = (
    entries: RabEntry[],
    categoryOrder: string[] = DEFAULT_CATEGORY_ORDER,
): CategoryGroup[] => {
    const groups = new Map<string, { group: CategoryGroup; firstIndex: number }>();
    entries.forEach((entry, index) => {
        const key = categoryKey(entry.category);
        const existing = groups.get(key);
        if (existing) {
            existing.group.entries.push(entry);
        } else {
            groups.set(key, { group: { category: displayCategory(entry.category), entries: [entry] }, firstIndex: index });
        }
    });

    const orderKeys = (categoryOrder.length ? categoryOrder : DEFAULT_CATEGORY_ORDER).map(categoryKey);
    const rank = (key: string): number => {
        const i = orderKeys.indexOf(key);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };

    return [...groups.entries()]
        .sort((a, b) => rank(a[0]) - rank(b[0]) || a[1].firstIndex - b[1].firstIndex)
        .map(([, value]) => value.group);
};

/** Die Einträge in Dokumentreihenfolge (Gruppe für Gruppe). */
export const orderEntriesForDocument = (entries: RabEntry[], categoryOrder?: string[]): RabEntry[] =>
    groupEntriesByCategory(entries, categoryOrder).flatMap(g => g.entries);

/**
 * Verschiebt einen Eintrag innerhalb SEINER Kategorie um eine Position.
 * Getauscht wird mit dem nächsten Eintrag derselben Kategorie in der flachen
 * Liste; Einträge anderer Kategorien dazwischen behalten ihre Plätze. Am Rand
 * der Gruppe passiert nichts (dieselbe Referenz kommt zurück).
 */
export const moveEntryWithinCategory = (
    entries: RabEntry[],
    id: string,
    direction: 'up' | 'down',
): RabEntry[] => {
    const index = entries.findIndex(e => e.id === id);
    if (index < 0) return entries;
    const key = categoryKey(entries[index].category);
    const step = direction === 'up' ? -1 : 1;
    let neighbour = index + step;
    while (neighbour >= 0 && neighbour < entries.length && categoryKey(entries[neighbour].category) !== key) {
        neighbour += step;
    }
    if (neighbour < 0 || neighbour >= entries.length) return entries;
    const next = [...entries];
    [next[index], next[neighbour]] = [next[neighbour], next[index]];
    return next;
};

/** Ob der Eintrag in seiner Kategorie noch weiter in diese Richtung kann. */
export const canMoveWithinCategory = (entries: RabEntry[], id: string, direction: 'up' | 'down'): boolean =>
    moveEntryWithinCategory(entries, id, direction) !== entries;

/**
 * Verschiebt einen Eintrag in eine ANDERE Kategorie und hängt ihn dort ans
 * Ende: in der flachen Liste direkt hinter den letzten Eintrag der
 * Zielkategorie, gibt es noch keinen, ganz ans Ende. Gleiche Kategorie,
 * leerer Name oder unbekannte id: dieselbe Referenz kommt zurück.
 */
export const moveEntryToCategory = (
    entries: RabEntry[],
    id: string,
    category: string,
): RabEntry[] => {
    const index = entries.findIndex(e => e.id === id);
    const target = category.trim();
    if (index < 0 || !target) return entries;
    const key = categoryKey(target);
    if (categoryKey(entries[index].category) === key) return entries;

    const moved: RabEntry = { ...entries[index], category: target };
    const rest = entries.filter((_, i) => i !== index);
    let insertAt = rest.length;
    for (let i = rest.length - 1; i >= 0; i--) {
        if (categoryKey(rest[i].category) === key) {
            insertAt = i + 1;
            break;
        }
    }
    return [...rest.slice(0, insertAt), moved, ...rest.slice(insertAt)];
};

/**
 * Ordnet einen Eintrag per Stichwort einer Standardkategorie zu (z. B.
 * „Elektro" → Technik). Ohne Treffer bleibt die Kategorie, wie sie ist.
 */
export const autoCategorize = <T extends Partial<Omit<RabEntry, 'id'>>>(entry: T): T => {
    const textToSearch = `${entry.component || ''} ${entry.activity || ''} ${entry.hazards || ''} ${entry.measures || ''}`.toLowerCase();
    let newCategory = entry.category;

    if (textToSearch.includes('technik') || textToSearch.includes('tga') || textToSearch.includes('heizung') || textToSearch.includes('lüftung') || textToSearch.includes('sanitär') || textToSearch.includes('elektro')) {
        newCategory = 'Technik';
    } else if (textToSearch.includes('dach')) {
        newCategory = 'Dach';
    } else if (textToSearch.includes('fassade') || textToSearch.includes('fenster')) {
        newCategory = 'Fassade';
    } else if (textToSearch.includes('außenanlagen') || textToSearch.includes('grün')) {
        // Singular wie in der Standardreihenfolge — der alte Plural
        // sortierte sich in der Liste ans Ende (categoryKey kennt beide).
        newCategory = 'Außenanlage';
    } else if (textToSearch.includes('innenraum') || textToSearch.includes('ausbau')) {
        newCategory = 'Innenraum';
    } else if (textToSearch.includes('tragwerk') || textToSearch.includes('rohbau')) {
        newCategory = 'Tragwerk';
    } else if (textToSearch.includes('aufzug')) {
        newCategory = 'Aufzug';
    } else if (textToSearch.includes('tiefgarage')) {
        newCategory = 'Tiefgarage';
    }

    return { ...entry, category: newCategory || entry.category };
};

/**
 * Kategorie eines NEUEN Eintrags. Hat der Anwender sie im Formular selbst
 * eingetippt oder gewählt, gilt seine Wahl — vorher setzte autoCategorize sie
 * still um (Bauteil „Elektroverteilung" mit Kategorie „Dach" landete unter
 * Technik). Leeres Feld, KI-Vorschlag oder Vorbelegung aus der Planungsphase:
 * Stichwortzuordnung wie bisher, sie bringt freie KI-Kategorien („Elektro",
 * „Haustechnik") auf die Standardliste.
 */
export const categorizeNewEntry = <T extends Partial<Omit<RabEntry, 'id'>>>(entry: T, categoryByHand: boolean): T =>
    categoryByHand && (entry.category || '').trim() ? entry : autoCategorize(entry);

/**
 * Eingestellte Reihenfolge plus alle Kategorien, die in den Einträgen
 * vorkommen, aber nicht in der Einstellung stehen (angehängt in Reihenfolge
 * des ersten Auftretens) — damit der Einstellungsdialog auch eine
 * selbst angelegte Kategorie verschieben kann.
 */
export const categoryOrderWithExtras = (categoryOrder: string[], entries: RabEntry[]): string[] => {
    const base = categoryOrder.length ? [...categoryOrder] : [...DEFAULT_CATEGORY_ORDER];
    const known = new Set(base.map(categoryKey));
    for (const entry of entries) {
        const key = categoryKey(entry.category);
        if (!known.has(key)) {
            known.add(key);
            base.push(displayCategory(entry.category));
        }
    }
    return base;
};
